import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  IconButton,
  FormControlLabel,
  Radio,
  RadioGroup,
} from "@mui/material";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { useSnackbar } from "notistack";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Add, AddCircle, Delete, Remove } from "@mui/icons-material";
import {
  createAssessmentsAPIEndpoint,
  EndPoints,
} from "./../../../../services/AssessmentAPI";
import {
  formatDateTimeLocal,
  toUtcISOString,
} from "../../../../context/AuthContext";

function ExamDetailsDialog({ open, onClose, selectedExam }) {
  const [Exam, setExam] = useState(null);
  const [mode, setMode] = useState("view"); // view | editCore | editQuestions
  const { enqueueSnackbar } = useSnackbar();
  const schema = yup.object().shape({
    title: yup.string().required("Title is required"),
    description: yup.string().required("Description is required"),
    opensAtUtc: yup.date().required("Open date is required"),
    closesAtUtc: yup.date().nullable(),
    durationMinutes: yup
      .number()
      .typeError("Must be a number")
      .required("Duration Minutes required")
      .min(15, "Duration Minutes be at least 15 minutes"),
    questions: yup
      .array()
      .of(
        yup.object().shape({
          questionText: yup.string().required("Question text is required"),
          options: yup
            .array()
            .of(yup.string().required("Option cannot be empty"))
            .min(2, "At least two options required"),
          points: yup
            .number()
            .typeError("Must be a number")
            .required("Points are required")
            .min(1, "Points must be between 1 and 10")
            .max(10, "Points must be between 1 and 10"),
        }),
      )
      .min(1, "At least one question is required"),
  });

  const { control, handleSubmit, reset } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      title: "",
      description: "",
      opensAtUtc: "",
      closesAtUtc: "",
      durationMinutes: "30",
      questions: [],
    },
  });

  const handleOnClose = () => {
    onClose();
    setMode("view");
  };
  const {
    fields: questions,
    append,
    remove,
  } = useFieldArray({
    control,
    name: "questions",
  });

  // ✅ Fetch details
  useEffect(() => {
    if (open && selectedExam) {
      createAssessmentsAPIEndpoint(
        EndPoints.getAssessmentDetails(selectedExam.id),
      )
        .fetch()
        .then((res) => {
          setExam(res.data);
          reset({
            ...res.data,
            opensAtUtc: formatDateTimeLocal(res.data.opensAtUtc),
            closesAtUtc: formatDateTimeLocal(res.data.closesAtUtc),
          });
          console.log(res);
        })
        .catch(() =>
          enqueueSnackbar("Failed to load Exam details.", {
            variant: "error",
          }),
        );
    }
  }, [open, selectedExam, reset, enqueueSnackbar]);

  const handleSaveCore = (data) => {
    console.log("Form submitted!", data);
    createAssessmentsAPIEndpoint(EndPoints.updateCore(selectedExam.id))
      .put({
        ...data,
        opensAtUtc: toUtcISOString(data.opensAtUtc),
        closesAtUtc: toUtcISOString(data.closesAtUtc),
      })
      .then(() => {
        enqueueSnackbar("Core data updated!", { variant: "success" });
        setMode("view");
        setExam((prev) => ({ ...prev, ...data }));
        console.log(Exam)
      })
      .catch((err) => enqueueSnackbar(err.response.data, { variant: "error" }));
  };

  const handleSaveQuestions = (data) => {
    createAssessmentsAPIEndpoint(EndPoints.updateQuestions(selectedExam.id))
      .put(data.questions)
      .then(() => {
        enqueueSnackbar("Questions updated!", { variant: "success" });
        setMode("view");
        setExam((prev) => ({ ...prev, questions: data.questions }));
        onClose();
      })
      .catch(() =>
        enqueueSnackbar("Failed to update questions", { variant: "error" }),
      );
  };

  return (
    <Dialog
      open={open}
      onClose={handleOnClose}
      fullWidth
      maxWidth="md"
      className="assessment-dialog"
    >
      <DialogTitle className="header">
        <span className="title"> Exam Details</span>
        {mode === "view" && (
          <Box sx={{ float: "right" }} className="edit-btns">
            <Button size="small" onClick={() => setMode("editCore")}>
              Edit Core
            </Button>
            <Button size="small" onClick={() => setMode("editQuestions")}>
              Edit Questions
            </Button>
          </Box>
        )}
      </DialogTitle>

      <DialogContent dividers className="content">
        {mode === "view" && Exam && (
          <>
            <div className="title">{Exam.title}</div>
            <div className="description">{Exam.description}</div>
            <div className="box-dates">
              <div className="date">
                Opens: {new Date(Exam.opensAtUtc).toLocaleString()}
              </div>
              <div className="date close">
                Closes:{" "}
                {Exam.closesAtUtc
                  ? new Date(Exam.closesAtUtc).toLocaleString()
                  : "N/A"}
              </div>
            </div>
            <div className="ch-allow">
              Allow Multiple Submissions:{" "}
              {Exam.allowMultipleSubmissions ? "Yes" : "No"}
            </div>

            <Box sx={{ mt: 1 }}>
              <div className="que-header">Questions</div>
              {Exam.questions?.map((q, i) => (
                <Box className="question-box" key={i}>
                  <Typography className="question-title">
                    Q{i + 1}: {q.questionText} ({q.points} pts)
                  </Typography>
                  <ul>
                    {q.options.map((opt, j) => (
                      <li
                        className="choices"
                        key={j}
                        style={{
                          color: j === q.correctOptionsIndex ? "green" : "",
                        }}
                      >
                        {opt}
                      </li>
                    ))}
                  </ul>
                </Box>
              ))}
            </Box>
          </>
        )}

        {/* ✅ Edit Core Data */}
        {mode === "editCore" && (
          <form onSubmit={handleSubmit(handleSaveCore)}>
            <Controller
              name="title"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  className="textField"
                  {...field}
                  label="Title"
                  fullWidth
                  margin="normal"
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
            <Controller
              name="description"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  className="textField"
                  margin="normal"
                  label="Description"
                  fullWidth
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
            <Controller
              name="durationMinutes"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  type="number"
                  margin="normal"
                  label="Duration Minutes"
                  fullWidth
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  sx={{
                    "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                      {
                        display: "none",
                      },
                    "& input[type=number]": {
                      MozAppearance: "textfield", // Firefox
                    },
                  }}
                />
              )}
            />
            <Controller
              name="opensAtUtc"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  type="datetime-local"
                  {...field}
                  label="Opens At"
                  fullWidth
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  margin="normal"
                />
              )}
            />
            <Controller
              name="closesAtUtc"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  type="datetime-local"
                  {...field}
                  label="Closes At"
                  fullWidth
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  margin="normal"
                />
              )}
            />
            <DialogActions>
              <Button onClick={() => setMode("view")}>Cancel</Button>
              <Button type="submit" variant="contained">
                Save
              </Button>
            </DialogActions>
          </form>
        )}

        {/* ✅ Edit Questions */}
        {mode === "editQuestions" && (
          <form onSubmit={handleSubmit(handleSaveQuestions)}>
            {questions.map((q, qIndex) => (
              <Box
                key={q.id}
                sx={{ p: 2, border: "1px solid #ddd", borderRadius: 2, mb: 2 }}
              >
                <Controller
                  name={`questions.${qIndex}.questionText`}
                  control={control}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label={`Question ${qIndex + 1}`}
                      margin="normal"
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />

                <Box>
                  <Controller
                    name={`questions.${qIndex}.correctOptionsIndex`}
                    control={control}
                    render={({ field: radioField, fieldState }) => (
                      <RadioGroup
                        value={radioField.value}
                        onChange={(e) =>
                          radioField.onChange(Number(e.target.value))
                        }
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      >
                        {q.options.map((_, optIndex) => (
                          <Box
                            key={optIndex}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                              mt: 3,
                            }}
                          >
                            <FormControlLabel
                              value={optIndex}
                              control={<Radio />}
                              label=""
                              sx={{ m: 0 }}
                            />

                            <Controller
                              name={`questions.${qIndex}.options.${optIndex}`}
                              control={control}
                              render={({ field, fieldState }) => (
                                <TextField
                                  {...field}
                                  fullWidth
                                  label={`Option ${optIndex + 1}`}
                                  error={!!fieldState.error}
                                  helperText={fieldState.error?.message}
                                />
                              )}
                            />
                          </Box>
                        ))}
                      </RadioGroup>
                    )}
                  />
                </Box>

                <Box className="form-grid-item">
                  <Controller
                    name={`questions.${qIndex}.points`}
                    control={control}
                    render={({ field, fieldState }) => (
                      <Box display="flex" alignItems="center" gap={1}>
                        <IconButton
                          onClick={() =>
                            field.onChange(Math.max(1, field.value - 1))
                          }
                        >
                          <Remove />
                        </IconButton>
                        <TextField
                          {...field}
                          type="number"
                          label="Points"
                          fullWidth
                          margin="normal"
                          helperText={fieldState.error?.message}
                          error={!!fieldState.error}
                          inputProps={{
                            min: 1,
                            max: 10,
                            readOnly: true,
                          }}
                        />

                        <IconButton
                          onClick={() => field.onChange(field.value + 1)}
                        >
                          <Add />
                        </IconButton>
                      </Box>
                    )}
                  />
                </Box>
                <IconButton color="error" onClick={() => remove(qIndex)}>
                  <Delete />
                </IconButton>
              </Box>
            ))}

            <Button
              variant="outlined"
              startIcon={<AddCircle />}
              onClick={() =>
                append({
                  questionText: "",
                  options: ["", "", "", ""],
                  correctOptionsIndex: 0,
                  points: 1,
                })
              }
            >
              Add Question
            </Button>
            <DialogActions>
              <Button onClick={() => setMode("view")}>Cancel</Button>
              <Button type="submit" variant="contained">
                Save Questions
              </Button>
            </DialogActions>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default ExamDetailsDialog;
